import { useEffect, useState, useRef } from 'react';
import { AppStorage } from '../Basic/storage-service';

const DEFAULT_TIMEOUT_MINUTES = 60; // fallback when portal settings don't specify one
const MAX_WARNING_MS = 2 * 60 * 1000; // show the warning at most 2 minutes before logout

// Idle timeout (ms) from portal settings, falling back to 60 minutes. The backend
// may expose portalSettingsAutoLogoutMinutes or portalSettingSessionTimeoutMinutes;
// if absent/invalid we use the default.
const getIdleTimeoutMs = () => {
    const settings = AppStorage.getPortalSetting() || {};
    const minutes = Number(settings.portalSettingsAutoLogoutMinutes) || Number(settings.portalSettingSessionTimeoutMinutes);
    const safeMinutes = Number.isFinite(minutes) && minutes > 0 ? minutes : DEFAULT_TIMEOUT_MINUTES;
    return safeMinutes * 60 * 1000;
};

export const useIdleTimeout = (onLogout) => {
    // Logout after the full idle window; warn 2 min before (or half the window if
    // the timeout is very small, so the warning always fits inside it).
    const logoutLimit = getIdleTimeoutMs();
    const warningDuration = Math.min(MAX_WARNING_MS, logoutLimit / 2);
    const idleLimit = logoutLimit - warningDuration;

    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(warningDuration / 1000);

    const warningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownIntervalRef = useRef(null);

    const isLoggedIn = AppStorage.isLoggedIn();

    // Records activity and resets this tab's timers.
    //
    // isFromSync distinguishes the TWO ways activity reaches this hook:
    //   - false (local): the user actually interacted with THIS tab. We write
    //     __last_activity__ to localStorage to broadcast the activity to other
    //     tabs, then reset our timers.
    //   - true (cross-tab): a storage event told us another tab saw activity.
    //     We reset our timers ONLY and must NOT write back to localStorage.
    //
    // Writing back on a sync-driven update is exactly the "ping-pong" trap:
    // Tab A writes -> Tab B's storage handler writes -> Tab A's storage handler
    // writes -> ... firing hundreds of times per second and freezing the main
    // thread. Gating the write on !isFromSync makes that loop impossible.
    const updateActivity = (isFromSync = false) => {
        const now = Date.now();
        // Strict === true: this is also exposed as `resetTimer`/`dismissWarning`
        // and called from an onClick handler, which would otherwise pass a truthy
        // event object here and wrongly suppress the cross-tab broadcast.
        if (isFromSync !== true) {
            AppStorage.setItem('__last_activity__', String(now));
        }
        resetTimers(now);
    };

    const resetTimers = (lastActivityTime) => {
        // Clear existing timers
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
        if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
        if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);

        setShowWarning(false);
        setCountdown(warningDuration / 1000);

        const now = Date.now();
        const timeSinceActivity = now - lastActivityTime;

        const warningTimeout = Math.max(0, idleLimit - timeSinceActivity);
        const logoutTimeout = Math.max(0, logoutLimit - timeSinceActivity);

        warningTimerRef.current = setTimeout(() => {
            setShowWarning(true);
            // Start countdown
            const startCountdownTime = Date.now();
            countdownIntervalRef.current = setInterval(() => {
                const secondsPassed = Math.floor((Date.now() - startCountdownTime) / 1000);
                const remaining = Math.max(0, (warningDuration / 1000) - secondsPassed);
                setCountdown(remaining);
                if (remaining <= 0) {
                    clearInterval(countdownIntervalRef.current);
                }
            }, 1000);
        }, warningTimeout);

        logoutTimerRef.current = setTimeout(() => {
            onLogout();
        }, logoutTimeout);
    };

    useEffect(() => {
        if (!isLoggedIn) return;

        // Initial load
        let lastActivity = Number(AppStorage.getItem('__last_activity__'));
        if (!lastActivity) {
            lastActivity = Date.now();
            AppStorage.setItem('__last_activity__', String(lastActivity));
        }
        resetTimers(lastActivity);

        // Event listeners for user activity
        const events = ['mousemove', 'keydown', 'mousedown', 'touchstart', 'scroll', 'click'];
        const handleActivity = () => {
            const lastUpdate = Number(AppStorage.getItem('__last_activity__')) || 0;
            if (Date.now() - lastUpdate > 5000) {
                updateActivity();
            }
        };

        events.forEach(e => window.addEventListener(e, handleActivity));

        // Storage listener for cross-tab sync.
        // __last_activity__ is obfuscated to the physical key _x15a_ in secure
        // mode (see KEY_MAP in storage-service), so match either name.
        const handleStorageChange = (e) => {
            const physicalKeyActivity = '_x15a_';
            if (e.key === '__last_activity__' || e.key === physicalKeyActivity) {
                // Cross-tab activity: reset OUR timers only. isFromSync=true
                // guarantees we do NOT write __last_activity__ back, so this
                // storage event does not re-broadcast and cannot ping-pong.
                updateActivity(true);
            }
        };
        window.addEventListener('storage', handleStorageChange);

        // Foreground re-evaluation (critical on mobile / Android Home button).
        // While the app is backgrounded, JS timers are frozen, so the idle
        // logout timer never fires. On return we recompute from wall-clock time:
        // resetTimers schedules logoutTimeout = max(0, limit - elapsed), so if the
        // idle window already passed it fires an immediate logout; otherwise it
        // reschedules correctly. Uses the shared __last_activity__ (no write back).
        const handleVisibility = () => {
            if (document.visibilityState === 'visible') {
                const last = Number(AppStorage.getItem('__last_activity__')) || Date.now();
                resetTimers(last);
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        window.addEventListener('pageshow', handleVisibility);

        return () => {
            events.forEach(e => window.removeEventListener(e, handleActivity));
            window.removeEventListener('storage', handleStorageChange);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pageshow', handleVisibility);
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
            if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
            if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
        };
        // Re-run if the login state or the configured timeout changes (e.g. portal
        // settings arrive after mount), so timers reflect the current window.
    }, [isLoggedIn, onLogout, logoutLimit]);

    // Reset/dismiss handlers are arg-less arrows so an onClick event can't leak in
    // as isFromSync. `dismissWarning` is the semantic name for the warning dialog's
    // "Stay Logged In" action; `resetTimer` is kept as an alias for existing callers.
    const dismissWarning = () => updateActivity(false);
    return { showWarning, countdown, resetTimer: dismissWarning, dismissWarning };
};
